/**
 * 일일 리포트 목록 페이지
 */

import { useState } from 'react';
import PaginationControls from '../components/common/PaginationControls';
import MarketScopeTabs from '../components/market/MarketScopeTabs';
import ReportCards from '../components/reports/ReportCards';
import ReportFilters from '../components/reports/ReportFilters';
import { useReportsPage } from '../hooks/useReportsPage';
import type { MarketScope } from '../types/marketScope';
import './Reports.css';
import '../components/market/MarketScope.css';

export default function Reports() {
  const [scope, setScope] = useState<MarketScope>('US');
  const [reportScope, setReportScope] = useState<'live' | 'paper'>('live');
  const state = useReportsPage(reportScope);

  if (state.loading) {
    return (
      <div className="reports-container">
        <div className="loading">리포트 로딩 중...</div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="reports-container">
        <div className="error">
          <p>API 오류: {state.error}</p>
          <button onClick={state.loadReports}>다시 시도</button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>일일 리포트</h1>
        <button onClick={state.loadReports} className="refresh-button">
          새로고침
        </button>
      </div>
      <MarketScopeTabs value={scope} onChange={setScope} includeAll={false} />

      {scope === 'KR' ? (
        <div className="empty">
          <p>KR 일일 리포트 데이터 없음</p>
          <p>국내장은 replay와 live shadow 검증만 있으며 KR daily report 생성기는 연결되지 않았습니다.</p>
          <p className="hint">더미 리포트는 만들지 않습니다. 실제 KR 운영 데이터가 생기면 표시합니다.</p>
        </div>
      ) : (
        <UsReports state={state} reportScope={reportScope} setReportScope={setReportScope} />
      )}
    </div>
  );
}

function UsReports({ state, reportScope, setReportScope }: {
  state: ReturnType<typeof useReportsPage>;
  reportScope: 'live' | 'paper';
  setReportScope: (scope: 'live' | 'paper') => void;
}) {
  const changeReportScope = (nextScope: 'live' | 'paper') => {
    setReportScope(nextScope);
    state.setOffset(0);
  };
  return (
    <>
      <div className="report-scope-tabs" aria-label="리포트 범위">
        <button className={reportScope === 'live' ? 'active' : ''}
          onClick={() => changeReportScope('live')} type="button">
          US Live pilot
        </button>
        <button className={reportScope === 'paper' ? 'active' : ''}
          onClick={() => changeReportScope('paper')} type="button">
          Paper archive
        </button>
      </div>
      <section className="report-scope-note">
        <strong>{reportScope === 'live' ? 'US Live pilot' : 'US Paper archive'}</strong>
        <span>
          {reportScope === 'live'
            ? 'Toss US live_pilot 전환 이후 리포트만 표시합니다. 첫날 기준금은 $300이며 과거 paper 성과와 합산하지 않습니다.'
            : '2026-06-30 이전 Alpaca paper 운영 리포트입니다. 현재 live_pilot 성과로 해석하지 않습니다.'}
        </span>
      </section>
      <ReportFilters
        startDate={state.startDate}
        endDate={state.endDate}
        onStartDateChange={state.setStartDate}
        onEndDateChange={state.setEndDate}
        onSearch={state.search}
      />

      {state.reportsPage.items.length === 0 ? (
        <div className="empty">
          <p>데이터 없음</p>
          <p>{reportScope === 'live' ? 'US live_pilot 리포트가 아직 생성되지 않았습니다.' : 'Paper archive 리포트가 없습니다.'}</p>
          <p className="hint">자동 생성 시간: 미국장 거래일 16:00 ET 이후</p>
        </div>
      ) : (
        <>
          <ReportCards reports={state.reportsPage.items} />
          <PaginationControls
            total={state.reportsPage.total}
            limit={state.reportsPage.limit}
            offset={state.reportsPage.offset}
            onPageChange={state.setOffset}
          />
        </>
      )}
    </>
  );
}
